import { useState } from 'react'
import { CRED_KINDS, CRED_KIND_LABELS, SYSTEM_ROLES, credentialTemplate } from '../../../shared/projects.js'
import { useSystems } from '../../lib/queries/systems.js'
import { Dialog } from '../ui/Dialog.jsx'
import { CommitInput, CredStatusSelect } from './fields.jsx'
import styles from './projects.module.css'

/** Systems in scope and the credentials we need from the client for each. */
export function SystemsTab({ project, api, canEdit }) {
  const systems = useSystems()
  const [pick, setPick] = useState('')
  const slug = pick || systems.data?.[0]?.slug || ''
  const [confirming, setConfirming] = useState(false)
  const chosen = (systems.data ?? []).find((s) => s.slug === slug)

  return (
    <>
      <div className={`${styles.row} ${styles.between}`}>
        <p className={styles.note} style={{ maxWidth: 620 }}>
          This list tracks each request and its status only. Secret values stay in the secret store and are referenced as {'{{secret:…}}'}.
        </p>
        {canEdit ? (
          <span className={styles.row}>
            <select className={styles.select} style={{ width: 'auto' }} value={slug} onChange={(e) => setPick(e.target.value)} aria-label="System to add">
              {(systems.data ?? []).map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
            <button type="button" className={styles.primary} disabled={!slug} onClick={() => setConfirming(true)}>
              Add system
            </button>
          </span>
        ) : null}
      </div>

      {project.systems.length === 0 ? (
        <div className={styles.empty}>No systems on this project. Add one to seed its Day-0 credential checklist.</div>
      ) : (
        project.systems.map((system) => <SystemBlock key={system.id} project={project} system={system} api={api} canEdit={canEdit} />)
      )}

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Add this system?"
        description={`This adds ${chosen?.name ?? 'the system'} to ${project.name} and creates its starting checklist of ${credentialTemplate(slug).length} items, each due 5 days from today. You can remove the system later.`}
        footer={
          <>
            <button type="button" className={styles.ghost} onClick={() => setConfirming(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primary}
              onClick={async () => {
                await api.addSystem(project.id, { slug })
                setConfirming(false)
              }}
            >
              Yes, add system
            </button>
          </>
        }
      />
    </>
  )
}

function SystemBlock({ project, system, api, canEdit }) {
  const [kind, setKind] = useState('api')
  const [label, setLabel] = useState('')
  const got = system.credentials.filter((c) => c.status === 'received' || c.status === 'verified').length
  const pct = system.credentials.length ? (got / system.credentials.length) * 100 : 0

  return (
    <section className={styles.sys}>
      <div className={styles.sysHead}>
        <div className={styles.sysName}>
          <h2>{system.name}</h2>
          <span className={styles.muted}>{[system.vendor, system.environment].filter(Boolean).join(' · ')}</span>
        </div>
        <select
          className={styles.select}
          style={{ width: 'auto' }}
          value={system.role}
          disabled={!canEdit}
          aria-label={`Role of ${system.name}`}
          onChange={(e) => api.patchSystem(project.id, system.id, { role: e.target.value })}
        >
          {SYSTEM_ROLES.map((r) => (
            <option key={r} value={r}>
              {r === 'source' ? 'Source' : 'Target'}
            </option>
          ))}
        </select>
        <CommitInput
          style={{ width: 130 }}
          value={system.environment}
          disabled={!canEdit}
          aria-label={`Environment of ${system.name}`}
          onCommit={(v) => v.trim() && api.patchSystem(project.id, system.id, { environment: v.trim() })}
        />
        <div className={styles.sysProgress}>
          <div className={`${styles.row} ${styles.between}`} style={{ marginBottom: 4 }}>
            <span className={styles.muted}>Received</span>
            <b className={styles.mono}>
              {got}/{system.credentials.length}
            </b>
          </div>
          <div className={styles.bar}>
            <i style={{ width: `${pct}%` }} />
          </div>
        </div>
        {canEdit ? (
          <button type="button" className={`${styles.ghost} ${styles.dangerGhost}`} onClick={() => api.removeSystem(project.id, system.id)}>
            Remove system
          </button>
        ) : null}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <span className={styles.srOnly}>Received</span>
              </th>
              <th>Type</th>
              <th>What we need</th>
              <th>Status</th>
              <th>Due</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {system.credentials.map((cred) => (
              <tr key={cred.id} className={cred.status === 'received' || cred.status === 'verified' ? styles.credDone : undefined}>
                <td>
                  <input
                    type="checkbox"
                    className={styles.check}
                    checked={cred.status === 'received' || cred.status === 'verified'}
                    disabled={!canEdit}
                    aria-label={`Mark “${cred.label}” as received`}
                    onChange={(e) => api.patchCredential(project.id, cred.id, { status: e.target.checked ? 'received' : 'requested' })}
                  />
                </td>
                <td>
                  <span className={styles.chip}>{CRED_KIND_LABELS[cred.kind]}</span>
                </td>
                <td>
                  <CommitInput value={cred.label} disabled={!canEdit} aria-label="What we need" onCommit={(v) => v.trim().length >= 2 && api.patchCredential(project.id, cred.id, { label: v })} />
                </td>
                <td>
                  <CredStatusSelect value={cred.status} disabled={!canEdit} label={`Status of ${cred.label}`} onChange={(status) => api.patchCredential(project.id, cred.id, { status })} />
                </td>
                <td>
                  <CommitInput type="date" value={cred.dueOn ?? ''} disabled={!canEdit} aria-label="Due date" onCommit={(v) => api.patchCredential(project.id, cred.id, { dueOn: v })} />
                </td>
                <td>
                  {canEdit ? (
                    <button type="button" className={`${styles.ghost} ${styles.dangerGhost}`} aria-label={`Remove ${cred.label}`} onClick={() => api.removeCredential(project.id, cred.id)}>
                      Remove
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {system.credentials.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.muted}>
                  No items listed.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {canEdit ? (
        <form
          className={styles.addRow}
          onSubmit={(e) => {
            e.preventDefault()
            if (label.trim().length < 2) return
            api.addCredential(project.id, system.id, { kind, label: label.trim() }).then(() => setLabel(''))
          }}
        >
          <select className={styles.select} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Type">
            {CRED_KINDS.map((k) => (
              <option key={k} value={k}>
                {CRED_KIND_LABELS[k]}
              </option>
            ))}
          </select>
          <input className={styles.input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Add a credential or access item" aria-label="New item" maxLength={160} />
          <button type="submit" className={styles.btn} disabled={label.trim().length < 2}>
            Add item
          </button>
        </form>
      ) : null}
    </section>
  )
}
